import React, { useState, useCallback, useEffect } from 'react';
import closeIcon from '../assets/close.svg';

const TextSelectionPopup = ({ selectedText, onAddToNotes, position }) => {
  if (!selectedText) return null;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        background: 'rgba(50, 50, 50, 0.85)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '8px',
        padding: '8px 16px',
        zIndex: 2000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: '500',
        fontFamily: "'Poppins', sans-serif",
        backdropFilter: 'blur(5px)',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
      }}
      onClick={(e) => {
        e.stopPropagation();
        onAddToNotes();
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.background = 'rgba(60, 60, 60, 0.9)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.background = 'rgba(50, 50, 50, 0.85)';
      }}
    >
      Add to Notes
    </div>
  );
};

const NotesModal = ({ isOpen, onClose, content, title, urlSources }) => {
  const [selectedText, setSelectedText] = useState('');
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    
    if (text) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const modalContainer = document.querySelector('[data-modal-container]');
      if (modalContainer) {
        const modalRect = modalContainer.getBoundingClientRect();
        
        // Calculate position relative to modal
        setPopupPosition({
          x: Math.min(rect.left - modalRect.left, modalRect.width - 120),
          y: Math.max(rect.top - modalRect.top - 40, 10) 
        });
        setSelectedText(text);
      }
    } else {
      setSelectedText('');
    }
  }, []);

  const handleAddToNotes = useCallback(() => {
    if (!selectedText) return;

    try {
      const existingNotes = localStorage.getItem('userNotes') || '';
      const timestamp = new Date().toLocaleString();
      const formattedNote = existingNotes 
        ? `${existingNotes}\n\n---\n\n>> From "${title}" (saved ${timestamp}):\n\n${selectedText}`
        : `>> From "${title}" (saved ${timestamp}):\n\n${selectedText}`;
        
      localStorage.setItem('userNotes', formattedNote);
      
      // Clear selection
      window.getSelection().removeAllRanges();
      setSelectedText('');
      
      // Notify Notes component
      window.dispatchEvent(new Event('notesUpdated'));

      // Show feedback
      const feedbackDiv = document.createElement('div');
      feedbackDiv.textContent = 'Added to notes!';
      feedbackDiv.style.cssText = `
        position: fixed;
        bottom: 70px;
        right: 20px;
        background: rgba(40, 40, 40, 0.85);
        color: white;
        padding: 10px 20px;
        border-radius: 8px;
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        font-weight: 500;
        z-index: 2001;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        backdropFilter: blur(5px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        animation: fadeIn 0.3s ease-out forwards;
      `;
      document.body.appendChild(feedbackDiv);
      
      // Add fade-out animation
      setTimeout(() => {
        feedbackDiv.style.animation = 'fadeOut 0.5s ease-out forwards';
        setTimeout(() => feedbackDiv.remove(), 500);
      }, 1500);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  }, [selectedText, title]);

  const handleMouseUp = useCallback((e) => {
    // Short delay to ensure selection is complete
    setTimeout(handleSelection, 10);
  }, [handleSelection]);

  // Click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Parse and format the content if it's a string
  const formattedContent = typeof content === 'string' 
    ? content
        .split('\n\n')
        .map((paragraph, i) => `<p key=${i}>${paragraph}</p>`)
        .join('')
    : content;

  return (
    <div style={modalStyles.overlay} onClick={handleOverlayClick}>
      <div 
        style={modalStyles.container}
        data-modal-container
        onMouseUp={handleMouseUp}
      >
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{title}</h2>
          <img 
            src={closeIcon} 
            alt="Close" 
            onClick={onClose} 
            style={modalStyles.closeButton} 
          />
        </div>
        
        <div style={modalStyles.contentWrapper}>
          <div 
            style={modalStyles.content} 
            dangerouslySetInnerHTML={{ __html: formattedContent }}
          />
        </div>
        
        {urlSources && urlSources.length > 0 && (
          <div style={modalStyles.sources}>
            <h4 style={modalStyles.sourcesTitle}>Related Links:</h4>
            <ul style={modalStyles.sourcesList}>
              {urlSources.map((source, index) => (
                <li key={index} style={modalStyles.sourceItem}>
                  <a 
                    href={source.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={modalStyles.sourceLink}
                  >
                    {source.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <TextSelectionPopup 
          selectedText={selectedText}
          position={popupPosition}
          onAddToNotes={handleAddToNotes}
        />
      </div>
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(5px)',
    animation: 'fadeIn 0.3s ease-out',
  },
  container: {
    width: '85%',
    maxWidth: '700px',
    maxHeight: '85vh',
    background: 'rgba(250, 250, 250, 0.95)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    position: 'relative',
    overflowY: 'hidden',
    backdropFilter: 'blur(10px)',
    fontFamily: "'Poppins', sans-serif",
    border: '1px solid rgba(255, 255, 255, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.4s ease-out',
  },
  header: {
    padding: '20px 30px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    position: 'sticky',
    top: '0',
    zIndex: 10,
  },
  closeButton: {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    opacity: 0.7,
    ':hover': {
      transform: 'scale(1.1)',
      opacity: 1,
    }
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    fontFamily: "'Poppins', sans-serif",
    flex: 1,
  },
  contentWrapper: {
    padding: '5px',
    overflowY: 'auto',
    flex: 1,
    maxHeight: 'calc(85vh - 140px)', // Account for header and potential sources
  },
  content: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#222',
    padding: '20px 30px',
    textAlign: 'left',
    fontFamily: "'Poppins', sans-serif",
    position: 'relative',
    userSelect: 'text',
    '& p': {
      marginBottom: '16px',
    },
    '& h3': {
      fontSize: '18px',
      fontWeight: '600',
      marginTop: '24px',
      marginBottom: '12px',
    },
    '& ul, & ol': {
      marginLeft: '20px',
      marginBottom: '16px',
    },
    '& li': {
      marginBottom: '8px',
    },
    '& blockquote': {
      borderLeft: '4px solid rgba(0,0,0,0.1)',
      paddingLeft: '16px',
      marginLeft: '0',
      fontStyle: 'italic',
      color: '#444',
    },
  },
  sources: {
    padding: '15px 30px 25px',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    background: 'rgba(245, 245, 245, 0.5)',
    backdropFilter: 'blur(5px)',
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
  sourcesTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#333',
  },
  sourcesList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
  },
  sourceItem: {
    margin: '6px 0',
  },
  sourceLink: {
    color: '#0066cc',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'color 0.2s ease',
    ':hover': {
      color: '#004499',
      textDecoration: 'underline',
    }
  },
};

// Add keyframe animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
    
    @keyframes slideUp {
      from { 
        transform: translateY(20px);
        opacity: 0;
      }
      to { 
        transform: translateY(0);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);
}

export default NotesModal;